import { isCloudHubApp, isHybridApp, isRTFApp, Resource } from '@mulesoft/monitoring-center-components/lib/models/resource';
import { TimeRange } from '@mulesoft/monitoring-center-components/lib/components/BuiltInDashboards/PreBuilt';
import { HybridApplication, TargetType } from '@mulesoft/monitoring-center-components/lib/models/resource/hybrid';
import { RTFApplication } from '@mulesoft/monitoring-center-components/lib/models/resource/rtf';
import { DashboardError, DashboardLoadingState } from '@mulesoft/monitoring-center-components';
import { Profile } from '../../../remote-api/CoreServicesRemoteApi';
import { CustomProfile } from './ApplicationNetwork';

const resolveDeploymentTarget = (resource: Resource): string | null => {
  if (isRTFApp(resource)) {
    return 'rtf';
  }
  if (isHybridApp(resource)) {
    return 'hybrid';
  }
  if (isCloudHubApp(resource)) {
    return 'cloudhub';
  }
  return null;
};

const resolveClusterId = (resource: Resource): string | null => {
  if (isRTFApp(resource)) {
    return (resource as RTFApplication).target.id!;
  }
  if (isHybridApp(resource)) {
    const target = (resource as HybridApplication).target;
    return target.type !== TargetType.SERVER ? target.id : null;
  }
  return null; // There's no concept of a clusterId for a CH app.
};

const resolveServerId = (resource: Resource): string | null => {
  if (isHybridApp(resource)) {
    const target = (resource as HybridApplication).target;
    return target.type === TargetType.SERVER ? target.id : null;
  }
  return null; // We don't have the worker Id of the CH app. We don't have the server Id of the rtf app.
};

const resolveAppName = (resource: Resource): string | null => {
  if (isRTFApp(resource) || isHybridApp(resource)) return resource.name!;
  if (isCloudHubApp(resource)) return resource.id;
  return null;
};

const buildRelativeUrl = (resource: Resource, range: TimeRange): string => {
  const organizationId = resource.organizationId;
  const environmentId = resource.environmentId;
  const appName = resolveAppName(resource);
  const deploymentTarget = resolveDeploymentTarget(resource);

  if (!organizationId || !environmentId || !appName || !deploymentTarget || !range) {
    return '';
  }

  let relativeUrl = `/visualizer/embed/monitoring/#/organizations/${organizationId}/environments/${environmentId}/${deploymentTarget}/${appName}` +
    `?startDate=${range.from}&endDate=${range.to}`;

  const clusterId = resolveClusterId(resource);
  if (clusterId) {
    relativeUrl += `&clusterId=${clusterId}`;
  }

  const serverId = resolveServerId(resource);
  if (serverId) {
    relativeUrl += `&serverId=${serverId}`;
  }
  return relativeUrl;
};

const asCustomProfile = (profile: Profile| undefined): CustomProfile | null => {
  if (!profile) {
    return null;
  }
  if (!profile.access_token) {
    return null;
  }
  return {
    id: profile.id,
    access_token: profile.access_token,
    organizationId: profile.organization.id,
  };
};

export const buildApplicationNetworkProps = (resource: Resource, range: TimeRange,
                                             profile: Profile | undefined,
                                             onDashboardStateChange: (loading: DashboardLoadingState, uri: string,
                                                                      iframe?: HTMLIFrameElement) => void,
                                             onDashboardError: (error: DashboardError) => void) => {
  return {
    relativeUrl: buildRelativeUrl(resource, range),
    profile: asCustomProfile(profile),
    onDashboardStateError: onDashboardError,
    onDashboardStateChange: onDashboardStateChange,
  }
};
